import type { Locale } from "./site";

export type SearchDocumentType = "page" | "product" | "blog" | "support" | "legal" | "app";

export type SearchDocument = {
  id: string;
  locale: Locale;
  type: SearchDocumentType;
  path: string;
  title: string;
  description: string;
  body: string;
  tags: string[];
  // Discontinued/EOL pages (the legacy model archive). Kept searchable but always
  // ranked below current pages — see the sort in searchDocuments.
  archived: boolean;
  // Present only for scheduled blog posts in generated indexes. Search keeps
  // them hidden until this UTC calendar date unless preview mode is enabled.
  publishedAt?: string;
};

export type SearchResult = SearchDocument & {
  score: number;
  excerpt: string;
};

// Each locale's index is generated into its own sibling file, so the bundler
// splits them into separate chunks and the search route downloads only the
// active locale's documents when it mounts. Mirrors the import.meta.glob pattern
// used for blog content in `src/lib/blog.ts`.
const indexLoaders = import.meta.glob<{ documents: SearchDocument[] }>(
  "../generated/search-index.*.ts",
);

const SHOW_PREVIEW_SEARCH =
  import.meta.env.DEV ||
  (import.meta.env as unknown as Record<string, string | undefined>).VITE_INCLUDE_DRAFTS === "true";

export async function loadSearchIndex(locale: Locale): Promise<SearchDocument[]> {
  const loader = indexLoaders[`../generated/search-index.${locale}.ts`];
  if (!loader) return [];
  const { documents } = await loader();
  return documents;
}

type SearchOptions = {
  preview?: boolean;
  now?: Date;
};

export function searchDocuments(
  documents: SearchDocument[],
  query: string,
  limit = 12,
  options?: SearchOptions,
): SearchResult[] {
  const normalizedQuery = normalize(query);
  const tokens = tokenize(query);
  if (!tokens.length) return [];

  return (
    documents
      .filter((document) => isVisibleSearchDocument(document, options))
      .map((document) => {
        const score = scoreDocument(document, normalizedQuery, tokens);
        if (score <= 0) return null;

        return {
          ...document,
          score,
          excerpt: buildExcerpt(document, tokens),
        };
      })
      .filter((result): result is SearchResult => Boolean(result))
      // Archived (legacy/EOL) pages always sink below current pages; within each
      // group, sort by relevance score, then title.
      .sort(
        (a, b) =>
          Number(a.archived) - Number(b.archived) ||
          b.score - a.score ||
          a.title.localeCompare(b.title),
      )
      .slice(0, limit)
  );
}

function isVisibleSearchDocument(document: SearchDocument, options?: SearchOptions): boolean {
  const preview = options?.preview ?? (options?.now ? false : SHOW_PREVIEW_SEARCH);
  if (preview) return true;
  if (!document.publishedAt) return true;
  return document.publishedAt <= (options?.now ?? new Date()).toISOString().slice(0, 10);
}

function scoreDocument(document: SearchDocument, normalizedQuery: string, tokens: string[]) {
  const title = normalize(document.title);
  const description = normalize(document.description);
  const body = normalize(document.body);
  const tags = normalize(document.tags.join(" "));

  let score = 0;

  if (title.includes(normalizedQuery)) score += 32;
  if (description.includes(normalizedQuery)) score += 18;
  if (body.includes(normalizedQuery)) score += 10;
  if (tags.includes(normalizedQuery)) score += 14;

  for (const token of tokens) {
    if (title.includes(token)) score += 14;
    if (description.includes(token)) score += 8;
    if (tags.includes(token)) score += 7;
    if (body.includes(token)) score += 3;
  }

  // Nudge current catalog products above general/service pages for ambiguous
  // queries. Only applied to docs that already matched, so it never surfaces an
  // otherwise-irrelevant product. Archived products are excluded (they sink anyway).
  if (score > 0 && document.type === "product" && !document.archived) score += 10;

  return score;
}

function buildExcerpt(document: SearchDocument, tokens: string[]) {
  const source = document.description || document.body;
  const normalizedSource = normalize(source);
  const token = tokens.find((candidate) => normalizedSource.includes(candidate));
  if (!token || source.length <= 180) return source;

  const tokenIndex = normalizedSource.indexOf(token);
  const start = Math.max(0, tokenIndex - 70);
  const end = Math.min(source.length, tokenIndex + 140);
  const excerpt = source.slice(start, end).trim();

  return `${start > 0 ? "... " : ""}${excerpt}${end < source.length ? " ..." : ""}`;
}

function tokenize(value: string) {
  return normalize(value)
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 1);
}

function normalize(value: string) {
  return (
    value
      .toLocaleLowerCase()
      .normalize("NFKD")
      // Apostrophes that look equivalent can belong to different Unicode
      // categories. In particular, Ukrainian `ʼ` has the Diacritic property, so
      // canonicalize it before stripping diacritics; `’` and `'` must produce the
      // same word boundary.
      .replace(/['’‘ʼʻꞌ＇]/gu, " ")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/[^\p{Letter}\p{Number}]+/gu, " ")
      .trim()
  );
}
